import { NavigateFunction } from 'react-router';
import { BackgroundProcess } from 'src/data-capture/hooks/file-uploads/types';
import { EmailDocumentType } from 'src/meeting-note/hooks/useGenerateEmailContent';
import { Flags } from 'src/feature-flags/FlagsHook';
import { Action, ActionItem } from '../files-table/FilesTable';
import { GeneratedDocumentType } from './generatedDocumentTypes';
import type { DocumentEditOwner } from 'src/document-editor/documentOwner';

type GeneratedDocMenuDoc = {
  id: string;
  documentType: GeneratedDocumentType;
  fileId?: number;
  documentHistoryId?: string;
  generatedFromAgenda?: boolean;
  backgroundProcess?: BackgroundProcess;
  fileName?: string;
};

type GeneratedDocMenuFlags = Pick<
  Flags,
  'flagEnabledDocumentEdit' | 'flagEnabledMeetingNoteEdit' | 'flagEnabledAgendaMeetingNoteReadOnly'
>;

type BuildGeneratedDocActionsParams = {
  doc: GeneratedDocMenuDoc;
  onEmail?: () => void;
  onSelect?: () => void;
  onUploadToSharepoint?: () => void;
  onDelete: () => void;
};

export function buildGeneratedDocActions({
  doc,
  onEmail,
  onSelect,
  onUploadToSharepoint,
  onDelete,
}: BuildGeneratedDocActionsParams): Action<unknown>[] {
  const actions: Action<unknown>[] = [];

  const emailReady = (doc.backgroundProcess?.status ?? 'SUCCESS') === 'SUCCESS';
  if (onEmail && emailTypeFor(doc) && emailReady) {
    actions.push({ label: 'Draft email', action: () => onEmail() });
  }

  if (onSelect) {
    actions.push({ label: 'Select', action: onSelect });
  }

  if (onUploadToSharepoint && doc.fileId != null) {
    actions.push({ label: 'Upload to SharePoint', action: () => onUploadToSharepoint() });
  }

  actions.push({ label: 'Delete', color: 'red', action: onDelete });

  return actions;
}

function editRouteFor(doc: GeneratedDocMenuDoc, owner: DocumentEditOwner): string {
  const base = owner.clientId
    ? `/client/${owner.clientId}/document`
    : `/family/${owner.familyId}/documents`;
  switch (doc.documentType) {
    case 'suitability-report':
      return `${base}/suitability-reports/${doc.id}/edit`;
    case 'meeting-agenda':
      return `${base}/meeting-agendas/${doc.id}/edit`;
    case 'data-export':
      return `${base}/data-exports/${doc.id}/edit`;
    case 'meeting-note':
      return `${base}/meeting-notes/${doc.id}/edit`;
  }
}

export function emailTypeFor(
  doc: Pick<GeneratedDocMenuDoc, 'documentType'>,
): EmailDocumentType | undefined {
  switch (doc.documentType) {
    case 'meeting-note':
      return 'MEETING_NOTE';
    case 'meeting-agenda':
      return 'MEETING_AGENDA';
    default:
      return undefined;
  }
}

export function buildEditAction(
  doc: GeneratedDocMenuDoc,
  owner: DocumentEditOwner | null,
  navigate: NavigateFunction,
  flags: GeneratedDocMenuFlags,
  fromPath?: string,
): ActionItem<unknown> | null {
  if (!owner || !generationSucceeded(doc)) return null;
  const route = editRouteFor(doc, owner);

  if (doc.documentType === 'suitability-report') {
    if (!flags.flagEnabledDocumentEdit) return null;
    return editAction(route, navigate, fromPath);
  }

  if (doc.documentType === 'meeting-agenda') {
    if (!flags.flagEnabledDocumentEdit || !doc.documentHistoryId) return null;
    return editAction(route, navigate, fromPath);
  }

  if (doc.documentType === 'data-export') {
    if (!flags.flagEnabledDocumentEdit || !doc.documentHistoryId || isSpreadsheet(doc.fileName)) {
      return null;
    }
    return editAction(route, navigate, fromPath);
  }

  const canEdit = flags.flagEnabledMeetingNoteEdit && !!doc.documentHistoryId;
  if (!canEdit) return null;

  const disabledForAgenda = flags.flagEnabledAgendaMeetingNoteReadOnly && !!doc.generatedFromAgenda;
  if (disabledForAgenda) {
    return {
      label: 'Edit',
      disabled: true,
      tooltip: 'Notes generated from an agenda cannot be edited yet.',
      action: () => {},
    };
  }

  return editAction(route, navigate, fromPath);
}

function generationSucceeded(doc: GeneratedDocMenuDoc): boolean {
  return (doc.backgroundProcess?.status ?? 'SUCCESS') === 'SUCCESS';
}

function isSpreadsheet(fileName?: string): boolean {
  return fileName ? /\.(xlsx|xls|csv)$/i.test(fileName) : false;
}

function editAction(
  route: string,
  navigate: NavigateFunction,
  fromPath?: string,
): ActionItem<unknown> {
  return {
    label: 'Edit',
    action: () => {
      if (fromPath) {
        navigate(route, { state: { fromPath } });
        return;
      }
      navigate(route);
    },
  };
}
